import React, { useEffect, useState } from "react";
import { Image, Upload, message } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import type { UploadProps } from "antd";
import container from "@/lib/di/container";
import { MediaRepo } from "@/core/repos/tables/media.repo";

const mediaRepo = container.get(MediaRepo);

interface ArticleCoverUploadProps {
    articleId?: string;
    onEnsureArticleId: () => Promise<string | null>;
    disabled?: boolean;
}

export const ArticleCoverUpload: React.FC<ArticleCoverUploadProps> = ({
    articleId,
    onEnsureArticleId,
    disabled = false,
}) => {
    const [coverUrl, setCoverUrl] = useState<string | null>(null);
    const [coverMediaId, setCoverMediaId] = useState<string | null>(null);
    const [uploading, setUploading] = useState(false);
    const [messageApi, contextHolder] = message.useMessage();

    const loadCover = async (id: string) => {
        const mediaList = await mediaRepo.list("Article", id);
        const cover = mediaList.find((item) => item.name?.startsWith("cover-"));
        if (cover?.path) {
            setCoverUrl(cover.path);
            setCoverMediaId(cover.id);
        } else {
            setCoverUrl(null);
            setCoverMediaId(null);
        }
    };

    useEffect(() => {
        if (articleId) {
            loadCover(articleId);
        } else {
            setCoverUrl(null);
            setCoverMediaId(null);
        }
    }, [articleId]);

    const uploadProps: UploadProps = {
        accept: "image/*",
        showUploadList: false,
        disabled: disabled || uploading,
        beforeUpload: async (file) => {
            const resolvedArticleId = articleId ?? (await onEnsureArticleId());
            if (!resolvedArticleId) {
                return Upload.LIST_IGNORE;
            }

            setUploading(true);
            const hide = messageApi.loading("Đang tải ảnh đại diện...", 0);
            try {
                if (coverMediaId) {
                    await mediaRepo.deleteFiles([coverMediaId]);
                }

                const uploaded = await mediaRepo.uploadCompressed(
                    {
                        id: "",
                        name: `cover-${file.name}`,
                        objectType: "Article",
                        referenceId: resolvedArticleId,
                    },
                    file,
                    { quality: 85, maxWidth: 1200 }
                );

                if (!uploaded?.id) {
                    messageApi.error("Upload ảnh đại diện thất bại");
                    return Upload.LIST_IGNORE;
                }

                const publicUrl = await mediaRepo.resolvePublicUrl(
                    "Article",
                    resolvedArticleId,
                    uploaded.id
                );

                if (publicUrl) {
                    setCoverUrl(publicUrl);
                    setCoverMediaId(uploaded.id);
                    messageApi.success("Đã cập nhật ảnh đại diện");
                }
            } catch {
                messageApi.error("Có lỗi khi upload ảnh đại diện");
            } finally {
                hide();
                setUploading(false);
            }

            return Upload.LIST_IGNORE;
        },
    };

    const removeCover = async () => {
        if (!coverMediaId) return;
        setUploading(true);
        const result = await mediaRepo.deleteFiles([coverMediaId]);
        if (result.success > 0) {
            setCoverUrl(null);
            setCoverMediaId(null);
            messageApi.success("Đã xóa ảnh đại diện");
        } else {
            messageApi.error("Xóa ảnh đại diện thất bại");
        }
        setUploading(false);
    };

    return (
        <div>
            {contextHolder}
            <p
                className="mb-2 text-[16px] font-[400]"
                style={{ color: "var(--primary-label)" }}
            >
                Ảnh đại diện
            </p>
            <div className="flex items-start gap-3">
                <Upload {...uploadProps}>
                    <div
                        className="flex flex-col items-center justify-center border border-dashed rounded-lg cursor-pointer hover:border-primary"
                        style={{ width: 160, height: 120 }}
                    >
                        {coverUrl ? (
                            <Image
                                src={coverUrl}
                                alt="Ảnh đại diện"
                                preview
                                style={{
                                    width: 160,
                                    height: 120,
                                    objectFit: "cover",
                                    borderRadius: 8,
                                }}
                            />
                        ) : (
                            <>
                                <PlusOutlined />
                                <span className="mt-2 text-sm">Tải ảnh</span>
                            </>
                        )}
                    </div>
                </Upload>
                {coverUrl && !disabled && (
                    <button
                        type="button"
                        className="flex items-center gap-1 text-red-500 text-sm mt-1"
                        onClick={removeCover}
                        disabled={uploading}
                    >
                        <DeleteOutlined />
                        Xóa ảnh
                    </button>
                )}
            </div>
            <p className="text-xs text-gray-500 mt-2">
                Ảnh hiển thị trên danh sách tin. Upload qua Media S3 (không dùng field legacy `image`).
            </p>
        </div>
    );
};
